import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ErrorHandlerService {

    /**
     * Extract error message from various error response formats
     * @param error - The error object from HTTP response
     * @param defaultMessage - Default message if error parsing fails
     * @returns Formatted error message string
     */
    extractErrorMessage(error: any, defaultMessage: string = 'An error occurred. Please try again.'): string {
        if (!error || !error.error) {
            return defaultMessage;
        }

        const detail = error.error.detail;

        if (!detail) {
            return defaultMessage;
        }

        // Handle array format: {"detail": [{"msg": "..."}]}
        if (Array.isArray(detail)) {
            if (detail.length > 0 && detail[0].msg) {
                return detail[0].msg;
            }
            // If array has strings instead of objects
            if (detail.length > 0 && typeof detail[0] === 'string') {
                return detail[0];
            }
            return defaultMessage;
        }

        // Handle simple string format: {"detail": "..."}
        if (typeof detail === 'string') {
            return detail;
        }

        // Handle object format: {"detail": {"message": "..."}}
        if (typeof detail === 'object' && detail.message) {
            return detail.message;
        }

        return defaultMessage;
    }

    /**
     * Extract multiple error messages from validation errors
     * @param error - The error object from HTTP response
     * @returns Array of error messages
     */
    extractErrorMessages(error: any): string[] {
        if (!error || !error.error?.detail) {
            return ['An error occurred. Please try again.'];
        }

        const detail = error.error.detail;

        if (Array.isArray(detail)) {
            return detail.map(item => {
                if (typeof item === 'string') {
                    return item;
                }
                if (item && typeof item === 'object' && item.msg) {
                    return item.msg;
                }
                return 'Invalid input';
            }).filter(msg => msg && msg.trim() !== '');
        }

        if (typeof detail === 'string') {
            return [detail];
        }

        return ['An error occurred. Please try again.'];
    }

    /**
     * Check if error is a validation error (422 status)
     * @param error - The error object from HTTP response
     * @returns True if it's a validation error
     */
    isValidationError(error: any): boolean {
        return error?.status === 422;
    }

    /**
     * Check if error is an authentication error (401 status)
     * @param error - The error object from HTTP response
     * @returns True if it's an authentication error
     */
    isAuthenticationError(error: any): boolean {
        return error?.status === 401;
    }

    /**
     * Check if error is a server error (5xx status)
     * @param error - The error object from HTTP response
     * @returns True if it's a server error
     */
    isServerError(error: any): boolean {
        return error?.status >= 500 && error?.status < 600;
    }
}
